import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FlatButton } from '@framework/ui/atoms';
import * as EmailValidator from 'email-validator';
import { Link } from 'react-router';
import { UserPagePathBuilder } from '@user/pages/UserPagePathBuilder';
import { classNames } from '@framework/utils';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faExternalLinkAlt } from '@fortawesome/free-solid-svg-icons';
import { trpcClient } from '@framework/trpcClient';

const FooterLink: React.FC<unknown> = () => {
  const { t } = useTranslation();
  return (
    <div className="pt-8">
      <Link to={UserPagePathBuilder.loginPage()} className="text-sm text-blue-700 hover:underline">
        {t('auth.passwordReset.loginLink')}
      </Link>
      <br />
      <a
        href="https://balus-help.levii.co.jp/hc/ja/requests/new"
        target="_blank"
        rel="noreferrer"
        className="text-sm text-blue-700 hover:underline"
      >
        {t('auth.accountConnected.inquiryForm')} <FontAwesomeIcon icon={faExternalLinkAlt} />
      </a>
    </div>
  );
};

export const RequestForm: React.FC<unknown> = () => {
  const { t } = useTranslation();
  const [mailAddress, setMailAddress] = useState('');
  const [processing, setProcessing] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState('');

  const handleChangeMailAddress = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    setMailAddress(event.target.value);
  }, []);

  const handleClick = useCallback(async () => {
    setProcessing(true);

    try {
      await trpcClient.user.auth.sendPasswordResetToken.mutate({ mailAddress });
      setDone(true);
    } catch (error) {
      setError(t('auth.passwordReset.sendError'));
      console.error(error);
    } finally {
      setProcessing(false);
    }
  }, [mailAddress, t]);

  const isValid = useMemo(() => {
    return mailAddress.length > 0 && EmailValidator.validate(mailAddress);
  }, [mailAddress]);

  if (done) {
    return (
      <>
        <div className="text-2xl font-bold">{t('auth.passwordReset.emailSentTitle')}</div>
        <div className="pt-8">
          {t('auth.passwordReset.emailSentDescription')}
          <br />
          {t('auth.passwordReset.emailSentNote')}
        </div>

        <FooterLink />
      </>
    );
  }

  return (
    <>
      <div className="py-4">
        {t('auth.passwordReset.requestDescription1')}
        <br />
        {t('auth.passwordReset.requestDescription2')}
      </div>
      <input
        type="text"
        className={classNames('w-full rounded-sm border p-2', processing ? 'cursor-not-allowed bg-gray-200' : '')}
        value={mailAddress}
        onChange={handleChangeMailAddress}
        placeholder={t('auth.login.emailLabel')}
        autoFocus
        disabled={processing}
      />
      {error && <div className="text-red-500">{error}</div>}

      <div className="pt-8 text-center">
        <FlatButton
          className="px-16"
          color="brand"
          onClick={handleClick}
          disabled={!isValid || processing}
          loading={processing}
        >
          {t('common.action.send')}
        </FlatButton>
      </div>

      <FooterLink />
    </>
  );
};
